"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  LoaderCircle,
  LocateFixed,
  MapPin,
  PackageSearch,
  Search,
  Store,
} from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import CartNavActionClient from "@/components/nav/CartNavActionClient";
import HeaderAccountWidget from "@/components/nav/HeaderAccountWidget";
import MobileSearchResultsTabs, {
  SearchResultAvailability,
  SearchResultThumbnail,
} from "@/components/nav/MobileSearchResultsTabs";
import SearchCategoryDropdown from "@/components/nav/SearchCategoryDropdown";
import { BUSINESS_CATEGORIES, normalizeCategoryName } from "@/lib/businessCategories";
import YourBarrioLogo from "@/components/brand/YourBarrioLogo";
import YourBarrioLoader from "@/components/ui/YourBarrioLoader";
import { sortListingsByAvailability } from "@/lib/inventory";
import { AUTH_UI_RESET_EVENT } from "@/components/AuthProvider";
import { useLocation } from "@/components/location/LocationProvider";
import { useCurrentLocationAction } from "@/components/location/useCurrentLocationAction";
import {
  getLocationCacheKey,
  getLocationLabel,
  isZipLike,
  normalizeSelectedLocation,
} from "@/lib/location";
import {
  buildLocationSuggestionPath,
  LOCATION_SUGGESTION_LIMIT,
  LOCATION_SUGGESTION_MIN_QUERY_LENGTH,
} from "@/lib/location/suggestions";
import { markNavInProgress } from "@/lib/nav/safariNavGuard";

const SEARCH_CATEGORIES = ["All", ...BUSINESS_CATEGORIES.map((cat) => cat.name)];

const getInitialSearchTerm = (params) => (params?.get("q") || "").trim();

const getInitialCategory = (params) => {
  const currentCategory = (params?.get("category") || "").trim();
  const normalizedCurrent = normalizeCategoryName(currentCategory).toLowerCase();
  const matchedCategory = SEARCH_CATEGORIES.find(
    (category) => normalizeCategoryName(category).toLowerCase() === normalizedCurrent
  );
  return matchedCategory || "All";
};

export default function GlobalHeader({
  surface = "public",
  showSearch = true,
  forcedAuth = null,
  minimal = false,
}) {
  const pathname = usePathname() || "";
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user } = useAuth();
  const { location, hasLocation, setLocation, requestGpsLocation } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [homepageNavState, setHomepageNavState] = useState("hero");
  const mobileDrawerId = useMemo(
    () => `global-mobile-drawer-${surface || "public"}`,
    [surface]
  );

  const [searchTerm, setSearchTerm] = useState(() => getInitialSearchTerm(searchParams));
  const [selectedCategory, setSelectedCategory] = useState(() => getInitialCategory(searchParams));
  const [locationOpen, setLocationOpen] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [locationQueryEdited, setLocationQueryEdited] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationSuggestLoading, setLocationSuggestLoading] = useState(false);
  const [locationSuggestError, setLocationSuggestError] = useState(null);
  const [locationSuggestIndex, setLocationSuggestIndex] = useState(-1);
  const [locationSelectHint, setLocationSelectHint] = useState(null);
  const [searchResults, setSearchResults] = useState({
    items: [],
    businesses: [],
    places: [],
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchErrorKey, setSearchErrorKey] = useState("");
  const [resolvedSearchKey, setResolvedSearchKey] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const searchBoxRef = useRef(null);
  const mobileSearchBoxRef = useRef(null);
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const locationRef = useRef(null);
  const publicDesktopLocationRef = useRef(null);
  const publicMobileLocationRef = useRef(null);
  const navRef = useRef(null);
  const locationPrefillRef = useRef(false);
  const locationSuggestAbortRef = useRef(null);
  const locationSuggestReqIdRef = useRef(0);
  const locationSuggestSpinnerRef = useRef(null);
  const locationSuggestShownAtRef = useRef(0);
  const searchRequestIdRef = useRef(0);
  const lastQueryRef = useRef("");
  const locationLabel = getLocationLabel(location);
  const locationNoMatchMessage = isZipLike(locationInput)
    ? "No matches. Try another postal code."
    : "No matches. Try another city.";

  const handleCurrentLocationSuccess = useCallback(() => {
    router.refresh();
    setLocationInput("");
    setLocationSuggestions([]);
    setLocationSuggestIndex(-1);
    setLocationSelectHint(null);
    setLocationQueryEdited(false);
    setLocationOpen(false);
  }, [router]);
  const {
    currentLocationLoading,
    currentLocationError,
    clearCurrentLocationError,
    requestCurrentLocation,
  } = useCurrentLocationAction({
    requestGpsLocation,
    onSuccess: handleCurrentLocationSuccess,
  });

  const sortedSearchItems = useMemo(
    () => sortListingsByAvailability(searchResults.items || []),
    [searchResults.items]
  );

  const baseSearchPath = surface === "customer" ? "/customer/home" : "/listings";
  const listingPath = surface === "customer" ? "/customer/listings" : "/listings";
  const logoHref = surface === "customer" && user?.id ? "/customer/home" : "/";
  const suppressProtectedPrefetch = pathname === "/customer/onboarding";
  const legalOnboardingNext = (() => {
    const currentNext = searchParams?.get("next");
    if (
      currentNext &&
      currentNext.startsWith("/") &&
      !currentNext.startsWith("//") &&
      !currentNext.startsWith("/api/") &&
      !currentNext.startsWith("/_next/")
    ) {
      return currentNext;
    }
    return "/";
  })();
  const safeLogoHref =
    suppressProtectedPrefetch && logoHref === "/customer/home"
      ? `/customer/onboarding?next=${encodeURIComponent(legalOnboardingNext)}`
      : logoHref;
  const isCustomerFacingSearch = surface === "public" || surface === "customer";
  const isHomepageVideoSurface = surface === "public" && pathname === "/";
  const isHomepageHeroNav =
    isHomepageVideoSurface && homepageNavState === "hero";
  const homepageNavStyle = isHomepageHeroNav
    ? {
        "--yb-navbar-bg": "rgba(10, 12, 18, 0.88)",
        "--navbar-bg": "rgba(10, 12, 18, 0.88)",
        "--yb-navbar-border": "rgba(255, 255, 255, 0.12)",
        "--navbar-border": "rgba(255, 255, 255, 0.12)",
      }
    : undefined;
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_AUTH_DIAG !== "1") return;
    console.info("[AUTH_HEADER_RENDER]", {
      source: "GlobalHeader",
      surface,
      pathname,
      contextHasUser: Boolean(user?.id),
      forcedHasUser: Boolean(forcedAuth?.user?.id),
      forcedUserId: forcedAuth?.user?.id || null,
      forcedRole: forcedAuth?.role || null,
    });
  }, [forcedAuth?.role, forcedAuth?.user?.id, pathname, surface, user?.id]);
  const isNearbyPage =
    pathname === "/nearby" || pathname.startsWith("/customer/nearby");
  const isStickyRoute =
    isNearbyPage ||
    pathname === "/" ||
    pathname === "/b" ||
    pathname.startsWith("/b/") ||
    pathname === "/listings" ||
    pathname.startsWith("/listings/") ||
    pathname === "/privacy" ||
    pathname === "/terms" ||
    pathname === "/refund-policy" ||
    pathname === "/business-terms" ||
    pathname === "/prohibited-categories" ||
    pathname === "/cart" ||
    pathname.startsWith("/cart/") ||
    pathname === "/customer" ||
    pathname.startsWith("/customer/") ||
    pathname === "/account" ||
    pathname.startsWith("/account/");

  const hasHybridResults =
    (searchResults.items?.length || 0) +
      (searchResults.businesses?.length || 0) +
      (searchResults.places?.length || 0) >
    0;

  const activeSearchCategory = isCustomerFacingSearch ? "All" : selectedCategory;
  const normalizedSearchKey =
    searchTerm.trim().length >= 3 && hasLocation
      ? `${searchTerm.trim().toLowerCase()}::${activeSearchCategory.toLowerCase()}::${getLocationCacheKey(
          location
        ).toLowerCase()}`
      : "";
  const hasResolvedCurrentSearch =
    Boolean(normalizedSearchKey) && resolvedSearchKey === normalizedSearchKey;
  const mobileSearchError =
    searchErrorKey === normalizedSearchKey ? searchError : null;
  const mobileSearchPending =
    Boolean(normalizedSearchKey) && !hasResolvedCurrentSearch && !mobileSearchError;

  const navigateToSearch = (query, category) => {
    const value = (query || "").trim();
    const nextCategory = (category || "").trim();
    const params = new URLSearchParams();
    if (value) params.set("q", value);
    if (!isCustomerFacingSearch && nextCategory && nextCategory !== "All") {
      params.set("category", nextCategory);
    }
    const target = params.toString()
      ? `${baseSearchPath}?${params.toString()}`
      : baseSearchPath;
    setSuggestionsOpen(false);
    router.push(target);
  };

  const handleSubmitSearch = (event) => {
    event.preventDefault();
    navigateToSearch(searchTerm || "", selectedCategory);
  };

  const handleCategoryChange = (next) => {
    setSelectedCategory(next);
    navigateToSearch(searchTerm || "", next);
  };

  const handleSuggestionSelect = (value, itemId) => {
    const next = (value || "").trim();
    if (!next) return;
    setSearchTerm(next);
    setSuggestionsOpen(false);
    if (itemId) {
      router.push(`${listingPath}/${itemId}`);
      return;
    }
    navigateToSearch(next, selectedCategory);
  };

  const closeSearchDropdown = () => {
    setSuggestionsOpen(false);
    searchInputRef.current?.blur();
    mobileSearchInputRef.current?.blur();
  };

  const handleBusinessSuggestionKeyDown = (event, businessId) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    if (!businessId) return;
    closeSearchDropdown();
    router.push(`/b/${encodeURIComponent(businessId)}`);
  };

  const categorySelectWidth = Math.max(selectedCategory.length, 3) + 6;

  // Hybrid search — fetch AI-style blend of items + businesses
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 3) {
      queueMicrotask(() => {
        setSearchResults({ items: [], businesses: [], places: [] });
        setSearchError(null);
        setSearchErrorKey("");
        setSearchLoading(false);
        setSuggestionsOpen(false);
      });
      lastQueryRef.current = "";
      return;
    }

    if (!hasLocation) {
      queueMicrotask(() => {
        setSearchResults({ items: [], businesses: [], places: [] });
        setSearchError("Select a location to search.");
        setSearchErrorKey("");
        setSearchLoading(false);
        setSuggestionsOpen(false);
      });
      lastQueryRef.current = "";
      return;
    }

    const locationKey = getLocationCacheKey(location);
    const searchCategoryKey = isCustomerFacingSearch ? "All" : selectedCategory;
    const normalized = `${term.toLowerCase()}::${searchCategoryKey.toLowerCase()}::${locationKey.toLowerCase()}`;
    if (normalized === lastQueryRef.current) {
      queueMicrotask(() => {
        setSuggestionsOpen(true);
      });
      return;
    }

    const controller = new AbortController();
    const requestId = ++searchRequestIdRef.current;

    const handle = setTimeout(() => {
      queueMicrotask(() => {
        setSearchLoading(true);
        setSearchError(null);
        setSearchErrorKey("");
      });
      const categoryParam =
        !isCustomerFacingSearch && selectedCategory !== "All" ? selectedCategory : "";
      const params = new URLSearchParams();
      params.set("q", term);
      if (categoryParam) params.set("category", categoryParam);
      fetch(`/api/search?${params.toString()}`, {
        signal: controller.signal,
      })
        .then(async (res) => {
          if (!res.ok) {
            let message = "search_failed";
            try {
              const body = await res.json();
              message = body?.message || body?.error || message;
            } catch {
              // best effort
            }
            const err = new Error(message);
            err.code = res.status;
            throw err;
          }
          return res.json();
        })
        .then((data) => {
          if (searchRequestIdRef.current !== requestId) return;
          lastQueryRef.current = normalized;
          setSearchResults({
            items: data?.items || [],
            businesses: data?.businesses || [],
            places: data?.places || [],
          });
          setResolvedSearchKey(normalized);
          setSearchErrorKey("");
          setSuggestionsOpen(true);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (searchRequestIdRef.current !== requestId) return;
          const isRateLimited =
            err?.code === 429 || err?.message === "rate_limit_exceeded";
          setSearchError(
            isRateLimited
              ? "You are searching too fast. Please wait a moment."
              : err?.message || "Search is warming up. Try again in a moment."
          );
          setSearchErrorKey(normalized);
        })
        .finally(() => {
          if (searchRequestIdRef.current === requestId) {
            setSearchLoading(false);
          }
        });
    }, 450);

    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [searchTerm, selectedCategory, hasLocation, location, isCustomerFacingSearch]);

  // Close AI suggestions when clicking away
  useEffect(() => {
    if (!suggestionsOpen) return;
    const handleClick = (event) => {
      const clickedSearch =
        searchBoxRef.current?.contains(event.target) ||
        mobileSearchBoxRef.current?.contains(event.target);
      if (!clickedSearch) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [suggestionsOpen]);

  useEffect(() => {
    if (!locationOpen) return undefined;
    const handleClickOutside = (event) => {
      const clickedInsideLocation =
        locationRef.current?.contains(event.target) ||
        publicDesktopLocationRef.current?.contains(event.target) ||
        publicMobileLocationRef.current?.contains(event.target);
      if (!clickedInsideLocation) {
        setLocationOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setLocationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [locationOpen]);

  useEffect(() => {
    let timeoutId = null;
    if (!locationOpen) {
      locationPrefillRef.current = false;
      timeoutId = setTimeout(() => setLocationQueryEdited(false), 0);
      return () => clearTimeout(timeoutId);
    }
    if (locationPrefillRef.current) return;
    locationPrefillRef.current = true;
    timeoutId = setTimeout(() => {
      setLocationInput(locationLabel !== "Set location" ? locationLabel : "");
      setLocationQueryEdited(false);
      setLocationSuggestions([]);
      setLocationSuggestIndex(-1);
      setLocationSelectHint(null);
    }, 0);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [locationOpen, locationLabel]);

  useEffect(() => {
    const scheduled = new Set();
    const schedule = (fn) => {
      const id = setTimeout(fn, 0);
      scheduled.add(id);
    };
    const clearScheduled = () => {
      scheduled.forEach((id) => clearTimeout(id));
      scheduled.clear();
    };
    const abortInflight = () => {
      if (locationSuggestAbortRef.current) {
        locationSuggestAbortRef.current.abort();
        locationSuggestAbortRef.current = null;
      }
    };

    // Clear delayed spinner timer on every run (ONLY clear timer)
    if (locationSuggestSpinnerRef.current) {
      clearTimeout(locationSuggestSpinnerRef.current);
      locationSuggestSpinnerRef.current = null;
    }

    if (!locationOpen || !locationQueryEdited) {
      abortInflight();
      locationSuggestReqIdRef.current += 1; // invalidate pending
      locationSuggestShownAtRef.current = 0;
      schedule(() => setLocationSuggestLoading(false));
      schedule(() => setLocationSuggestError(null));
      schedule(() => setLocationSuggestIndex(-1));
      schedule(() => setLocationSuggestions([]));
      schedule(() => setLocationSelectHint(null));
      return clearScheduled;
    }

    const term = locationInput.trim();

    // If user cleared input, clear suggestions.
    if (term.length === 0) {
      abortInflight();
      locationSuggestReqIdRef.current += 1;
      locationSuggestShownAtRef.current = 0;
      schedule(() => setLocationSuggestLoading(false));
      schedule(() => setLocationSuggestError(null));
      schedule(() => setLocationSuggestIndex(-1));
      schedule(() => setLocationSuggestions([]));
      schedule(() => setLocationSelectHint(null));
      return clearScheduled;
    }

    // If term is short (1 char), keep last suggestions to prevent flicker.
    if (term.length < LOCATION_SUGGESTION_MIN_QUERY_LENGTH) {
      abortInflight();
      locationSuggestReqIdRef.current += 1;
      locationSuggestShownAtRef.current = 0;
      schedule(() => setLocationSuggestLoading(false));
      schedule(() => setLocationSuggestError(null));
      schedule(() => setLocationSuggestIndex(-1));
      schedule(() => setLocationSelectHint(null));
      return clearScheduled;
    }

    const reqId = ++locationSuggestReqIdRef.current;

    abortInflight();
    const controller = new AbortController();
    locationSuggestAbortRef.current = controller;

    // Delay spinner so it doesn't flash on fast responses.
    locationSuggestSpinnerRef.current = setTimeout(() => {
      if (reqId !== locationSuggestReqIdRef.current) return;
      locationSuggestShownAtRef.current = Date.now();
      setLocationSuggestLoading(true);
    }, 150);

    const handle = setTimeout(() => {
      setLocationSuggestError(null);

      const url = buildLocationSuggestionPath(term, location, {
        debug: process.env.NODE_ENV !== "production",
      });

      fetch(url, { signal: controller.signal })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data?.error || "location_suggest_failed");
          }
          return data;
        })
        .then((data) => {
          if (controller.signal.aborted) return;
          if (reqId !== locationSuggestReqIdRef.current) return;
          const next = Array.isArray(data?.suggestions)
            ? data.suggestions.slice(0, LOCATION_SUGGESTION_LIMIT)
            : [];
          setLocationSuggestions(next);
          setLocationSuggestIndex(-1);
          setLocationSelectHint(null);
          if (data?.error) {
            setLocationSuggestError(data.error);
          }
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (reqId !== locationSuggestReqIdRef.current) return;
          setLocationSuggestError(err?.message || "Location suggestions unavailable.");
          setLocationSuggestions([]);
          setLocationSuggestIndex(-1);
          setLocationSelectHint(null);
        })
        .finally(() => {
          if (controller.signal.aborted) return;
          if (reqId !== locationSuggestReqIdRef.current) return;
          if (locationSuggestSpinnerRef.current) {
            clearTimeout(locationSuggestSpinnerRef.current);
            locationSuggestSpinnerRef.current = null;
          }
          const shownAt = locationSuggestShownAtRef.current || 0;
          const elapsed = shownAt ? Date.now() - shownAt : 9999;
          const remaining = 300 - elapsed;

          if (remaining > 0) {
            setTimeout(() => {
              if (controller.signal.aborted) return;
              if (reqId !== locationSuggestReqIdRef.current) return;
              setLocationSuggestLoading(false);
            }, remaining);
          } else {
            setLocationSuggestLoading(false);
          }
        });
    }, 250);

    return () => {
      clearScheduled();
      clearTimeout(handle);
      controller.abort();
      if (locationSuggestSpinnerRef.current) {
        clearTimeout(locationSuggestSpinnerRef.current);
        locationSuggestSpinnerRef.current = null;
      }
    };
  }, [location, locationOpen, locationInput, locationQueryEdited]);

  useEffect(() => {
    if (!locationOpen) clearCurrentLocationError();
  }, [clearCurrentLocationError, locationOpen]);

  const applyLocationSuggestion = (suggestion) => {
    if (!suggestion) return;
    // We store city as the canonical location to match DB schema; zip is only used for lookup.
    setLocation(normalizeSelectedLocation(suggestion));
    router.refresh();
    setLocationInput("");
    setLocationSuggestions([]);
    setLocationSuggestIndex(-1);
    setLocationSelectHint(null);
    setLocationQueryEdited(false);
    setLocationOpen(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleReset = () => {
      setMobileMenuOpen(false);
      setSuggestionsOpen(false);
    };
    window.addEventListener(AUTH_UI_RESET_EVENT, handleReset);
    return () => window.removeEventListener(AUTH_UI_RESET_EVENT, handleReset);
  }, []);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    const nav = navRef.current || document.getElementById("yb-navbar");
    if (!nav) return undefined;

    const setHeight = () => {
      const measuredHeight = nav.getBoundingClientRect().height || nav.offsetHeight;
      const height = measuredHeight > 0 ? Math.round(measuredHeight) : 80;

      document.documentElement.style.setProperty(
        "--yb-nav-h",
        `${height}px`
      );
      document.documentElement.style.setProperty(
        "--yb-nav-content-offset",
        `${height}px`
      );
      document.documentElement.style.setProperty(
        "--yb-nav-layout-h",
        "0px"
      );
    };

    setHeight();
    window.addEventListener("resize", setHeight);

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(setHeight);
      observer.observe(nav);
    }

    return () => {
      window.removeEventListener("resize", setHeight);
      observer?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (!isHomepageVideoSurface || typeof window === "undefined") {
      return undefined;
    }

    let frameId = null;
    const syncHomepageNavState = () => {
      frameId = null;
      const hero = document.querySelector("[data-homepage-video-hero='1']");
      const nav = navRef.current || document.getElementById("yb-navbar");
      const hasNotice = Boolean(document.querySelector("[data-notice-id]"));
      const hasSupportBanner = Boolean(
        document.querySelector("[data-support-mode-banner='1']")
      );

      if (!hero || !nav || hasNotice || hasSupportBanner) {
        setHomepageNavState("solid");
        return;
      }

      const heroBox = hero.getBoundingClientRect();
      const navBox = nav.getBoundingClientRect();
      const heroProgress = Math.max(0, navBox.bottom - heroBox.top);
      const upperRegionThreshold = Math.min(
        190,
        Math.max(110, heroBox.height * 0.2)
      );
      const nextState =
        heroBox.bottom > navBox.bottom &&
        heroProgress <= upperRegionThreshold
          ? "hero"
          : "solid";
      setHomepageNavState((current) =>
        current === nextState ? current : nextState
      );
    };

    const scheduleSync = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(syncHomepageNavState);
    };

    syncHomepageNavState();
    window.addEventListener("scroll", scheduleSync, { passive: true });
    window.addEventListener("resize", scheduleSync);
    window.addEventListener("pageshow", scheduleSync);

    const mutationObserver =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(scheduleSync);
    mutationObserver?.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.removeEventListener("scroll", scheduleSync);
      window.removeEventListener("resize", scheduleSync);
      window.removeEventListener("pageshow", scheduleSync);
      mutationObserver?.disconnect();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [isHomepageVideoSurface]);

  const renderLocationDropdown = (
    panelClassName = "left-0 w-72",
    controlId = "global-location"
  ) => {
    const suggestionListId = `${controlId}-suggestions`;
    const hintId = `${controlId}-hint`;
    return locationOpen ? (
      <div className={`absolute top-full z-50 mt-3 rounded-2xl p-4 yb-dropdown-surface ${panelClassName}`}>
        <div className="text-xs uppercase tracking-[0.22em] yb-dropdown-title">Set location</div>
        <div id={hintId} className="mt-2 text-sm yb-dropdown-muted">Enter a city or ZIP code.</div>
        <div className="mt-4 flex items-center gap-2">
          <input
            type="text"
            value={locationInput}
            onChange={(event) => {
              setLocationInput(event.target.value);
              setLocationQueryEdited(true);
              setLocationSelectHint(null);
              clearCurrentLocationError();
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setLocationSuggestIndex((prev) =>
                  Math.min(prev + 1, locationSuggestions.length - 1)
                );
                return;
              }
              if (event.key === "ArrowUp") {
                event.preventDefault();
                setLocationSuggestIndex((prev) => Math.max(prev - 1, 0));
                return;
              }
              if (event.key === "Enter") {
                event.preventDefault();
                if (locationSuggestions.length > 0) {
                  const selected =
                    locationSuggestIndex >= 0 &&
                    locationSuggestions[locationSuggestIndex]
                      ? locationSuggestions[locationSuggestIndex]
                      : locationSuggestions[0];
                  applyLocationSuggestion(selected);
                  return;
                }
                setLocationSelectHint("Select a suggestion to set your location.");
              }
            }}
            placeholder="e.g. Austin, 78701"
            // Named like the visible "Set location" title, which is uppercased by CSS.
            aria-label="Set location"
            aria-describedby={hintId}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={locationSuggestions.length > 0}
            aria-controls={suggestionListId}
            aria-activedescendant={
              locationSuggestIndex >= 0
                ? `${controlId}-suggestion-${locationSuggestIndex}`
                : undefined
            }
            className="w-40 min-w-0 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
          />
        </div>
        <button
          type="button"
          onClick={() => void requestCurrentLocation()}
          disabled={currentLocationLoading}
          className="yb-location-action mt-2 inline-flex min-h-10 items-center gap-2 rounded-lg px-2 text-sm font-semibold transition hover:bg-purple-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/30 disabled:cursor-wait disabled:opacity-60"
          aria-busy={currentLocationLoading}
        >
          {currentLocationLoading ? (
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <LocateFixed className="h-4 w-4" aria-hidden="true" />
          )}
          {currentLocationLoading ? "Finding your location…" : "Use my current location"}
        </button>
        {currentLocationError ? (
          <div className="yb-location-error mt-1 text-xs leading-5" role="status">
            {currentLocationError}
          </div>
        ) : null}
        <div className="mt-3 text-xs yb-dropdown-muted min-h-[16px]">
          {locationSuggestLoading ? "Searching locations..." : ""}
        </div>
        {locationSuggestError ? (
          <div className="mt-3 text-xs text-rose-200">{locationSuggestError}</div>
        ) : null}
        {locationSelectHint ? (
          <div className="mt-2 text-xs yb-dropdown-muted">{locationSelectHint}</div>
        ) : null}
        {!locationSuggestLoading &&
        !locationSuggestError &&
        locationQueryEdited &&
        locationInput.trim().length >= LOCATION_SUGGESTION_MIN_QUERY_LENGTH &&
        locationSuggestions.length === 0 ? (
          <div className="mt-3 text-xs yb-dropdown-muted">
            {locationNoMatchMessage}
          </div>
        ) : null}
        {locationQueryEdited && locationSuggestions.length > 0 ? (
          <div className="mt-3">
            <div
              id={suggestionListId}
              role="listbox"
              className="max-h-56 overflow-y-auto rounded-xl border border-white/10 bg-white/5 p-1"
            >
              {locationSuggestions.map((suggestion, idx) => (
                <button
                  key={suggestion.id || suggestion.label || idx}
                  id={`${controlId}-suggestion-${idx}`}
                  type="button"
                  role="option"
                  aria-selected={idx === locationSuggestIndex}
                  onClick={() => applyLocationSuggestion(suggestion)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg yb-dropdown-item ${
                    idx === locationSuggestIndex ? "bg-white/10" : ""
                  }`}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    ) : null;
  };

  return (
    <nav
      id="yb-navbar"
      ref={navRef}
      className="fixed top-0 left-0 right-0 w-full z-50 theme-lock pointer-events-auto yb-navbar yb-navbar-bordered"
      data-nav-surface={surface}
      data-nav-sticky={isStickyRoute ? "1" : undefined}
      data-homepage-nav-state={
        isHomepageVideoSurface ? homepageNavState : undefined
      }
      data-nav-guard="1"
      data-testid={surface === "customer" ? "customer-navbar" : "global-navbar"}
      style={homepageNavStyle}
    >
      <div className="mx-auto flex h-20 w-full max-w-[1380px] items-center justify-between gap-3 px-4 sm:px-6 lg:gap-4 lg:px-7 xl:gap-5 xl:px-8">
        {!minimal ? (
          <button
            onClick={() => setMobileMenuOpen((open) => !open)}
            // Invisible 44x44 hit area around the 28px icon; layout and visuals are unchanged.
            className="relative text-white mr-0 after:absolute after:-inset-2 lg:hidden"
            aria-label="Open menu"
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileDrawerId}
          >
            <svg className="h-7 w-7" fill="none" stroke="currentColor">
              <path strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        ) : null}
        <Link
          href={safeLogoHref}
          prefetch={suppressProtectedPrefetch ? false : undefined}
          aria-label="Go to home"
          // Invisible 44x44 hit area around the 38px mark, leaning right to stay clear of the menu.
          className={`relative inline-flex shrink-0 cursor-pointer select-none touch-manipulation after:absolute after:-inset-y-[3px] after:-left-0.5 after:-right-1 ${
            !minimal ? "lg:mr-4 xl:mr-5" : ""
          }`}
          onPointerDownCapture={() => markNavInProgress(safeLogoHref)}
        >
          {minimal ? (
            <YourBarrioLogo
              variant="navbar-dark"
              alt="YourBarrio"
              sizes="170px"
              priority
              className="h-[46px] w-auto select-none"
              draggable={false}
            />
          ) : (
            <>
              {isHomepageHeroNav ? (
                <YourBarrioLogo
                  variant="mark-white"
                  alt="YourBarrio"
                  size={38}
                  priority
                  className="h-[38px] w-[38px] select-none lg:hidden"
                  draggable={false}
                />
              ) : (
                <YourBarrioLogo
                  variant="mark-purple"
                  alt="YourBarrio"
                  size={38}
                  priority
                  className="h-[38px] w-[38px] select-none lg:hidden"
                  draggable={false}
                />
              )}
              <YourBarrioLogo
                variant="navbar-dark"
                alt="YourBarrio"
                sizes="170px"
                priority
                className="hidden h-[46px] w-auto select-none lg:block"
                draggable={false}
              />
            </>
          )}
        </Link>

        {!minimal && !isCustomerFacingSearch ? (
          <div className="relative hidden lg:flex items-center" ref={locationRef}>
            <button
              type="button"
              onClick={() => setLocationOpen((open) => !open)}
              className={`flex h-11 max-w-[196px] items-center gap-2 rounded-xl border bg-white/[0.045] px-3 text-left text-white/85 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-colors duration-200 ease-out hover:bg-purple-500/[0.08] ${
                locationOpen ? "border-purple-400" : "border-white/12"
              }`}
              aria-haspopup="dialog"
              aria-expanded={locationOpen}
            >
              <MapPin className="h-4 w-4 shrink-0 text-white/70 transition-colors duration-200 ease-out hover:text-purple-400" />
              <div className="min-w-0 leading-tight">
                <div className="truncate text-sm font-medium">{locationLabel}</div>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 text-white/60 transition-colors duration-200 ease-out hover:text-purple-400" />
            </button>

            {renderLocationDropdown(undefined, "global-location-default")}
          </div>
        ) : null}

        {showSearch && !minimal ? (
          <div
            ref={mobileSearchBoxRef}
            className="relative flex min-w-0 flex-1 items-center justify-center gap-2 px-2 sm:px-4 lg:hidden lg:px-0"
            data-nav-guard="1"
          >
            <form
              onSubmit={handleSubmitSearch}
              onClick={(event) => {
                if (event.target instanceof HTMLButtonElement) return;
                if (publicMobileLocationRef.current?.contains(event.target)) return;
                mobileSearchInputRef.current?.focus();
              }}
              className={`relative flex w-full max-w-full min-w-0 flex-1 box-border items-center rounded-2xl border px-2 py-1.5 transition-[border-color,box-shadow] duration-200 ease-out focus-within:ring-2 md:w-full ${
                isCustomerFacingSearch
                  ? "gap-2 border-white/18 bg-white/[0.96] text-slate-950 shadow-[0_1px_0_rgba(255,255,255,0.08)] hover:border-white/28 focus-within:border-purple-300/80 focus-within:ring-purple-500/25"
                  : "gap-3 rounded-xl border-white/15 bg-white/10 hover:border-purple-400/60 focus-within:border-purple-400/70 focus-within:ring-purple-500/40"
              }`}
              style={{
                boxShadow: "0 0 0 0 rgba(124,58,237,0)",
              }}
            >
              {isCustomerFacingSearch ? (
                <div className="relative shrink-0" ref={publicMobileLocationRef}>
                  <button
                    type="button"
                    onClick={() => setLocationOpen((open) => !open)}
                    // Invisible 44x44 hit area around the 32px trigger.
                    className="relative flex h-8 items-center gap-1.5 rounded-xl px-2 text-slate-600 transition after:absolute after:-inset-1.5 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                    aria-label={`Set location. Current location: ${locationLabel}`}
                    aria-haspopup="dialog"
                    aria-expanded={locationOpen}
                  >
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </button>
                  {renderLocationDropdown(
                    "left-0 w-72 max-w-[calc(100vw-2rem)]",
                    "global-location-mobile"
                  )}
                </div>
              ) : null}
              <input
                id="global-nav-search-mobile"
                name="search"
                ref={mobileSearchInputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={(event) => {
                  setSuggestionsOpen(hasHybridResults || searchTerm.trim().length > 0);
                  event.currentTarget.form?.style.setProperty(
                    "box-shadow",
                    "0 0 0 2px rgba(124,58,237,0.25)"
                  );
                }}
                onBlur={(event) => {
                  event.currentTarget.form?.style.setProperty(
                    "box-shadow",
                    "0 0 0 0 rgba(124,58,237,0)"
                  );
                }}
                className={`yb-mobile-search-input h-8 min-w-0 flex-1 bg-transparent py-[3px] pr-10 text-base leading-[1.3] focus:outline-none md:text-sm ${
                  isCustomerFacingSearch
                    ? "text-slate-950 placeholder:text-slate-500"
                    : "placeholder:text-white/60"
                }`}
                placeholder={isCustomerFacingSearch ? "Search shops" : "Search local shops"}
                aria-label="Search shops and listings"
                type="search"
              />
              <button
                type="submit"
                aria-label="Search"
                // Invisible 44x44 hit area around the 28px pill; it stays clear of the input's text area.
                className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 transition after:absolute after:-inset-2 focus:outline-none focus:ring-2 focus:ring-purple-500/35 ${
                  isCustomerFacingSearch
                    ? "bg-purple-600 text-white hover:bg-purple-700"
                    : "bg-white text-black yb-navbar-light"
                }`}
              >
                <Search className="h-4 w-4" />
              </button>
            </form>
            <CartNavActionClient variant="mobile-navbar" />
            <MobileSearchResultsTabs
              key={searchTerm.trim().toLowerCase()}
              open={
                suggestionsOpen &&
                Boolean(normalizedSearchKey) &&
                (searchLoading ||
                  mobileSearchError ||
                  mobileSearchPending ||
                  hasResolvedCurrentSearch)
              }
              query={searchTerm}
              items={hasResolvedCurrentSearch ? sortedSearchItems.slice(0, 4) : []}
              businesses={
                hasResolvedCurrentSearch ? (searchResults.businesses || []).slice(0, 3) : []
              }
              loading={searchLoading || mobileSearchPending}
              error={mobileSearchError}
              onItemSelect={(item) => handleSuggestionSelect(item.title, item.id)}
              onClose={closeSearchDropdown}
            />
          </div>
        ) : null}

        {showSearch && !minimal ? (
          <div className="hidden flex-1 justify-center lg:flex" data-nav-guard="1">
            <div
              ref={searchBoxRef}
              className={`relative w-full ${
                isCustomerFacingSearch ? "max-w-[48rem] xl:max-w-[52rem]" : "max-w-[40rem] xl:max-w-[42rem]"
              }`}
              data-nav-guard="1"
            >
            <form
              onSubmit={handleSubmitSearch}
              className={`flex h-12 flex-1 items-stretch rounded-2xl border transition-[border-color,box-shadow] duration-200 ease-out focus-within:ring-2 ${
                isCustomerFacingSearch
                  ? "bg-white/[0.97] border-white/20 text-slate-950 shadow-[0_8px_20px_rgba(15,23,42,0.08)] hover:border-white/32 focus-within:border-purple-300/80 focus-within:ring-purple-500/25"
                  : "overflow-hidden rounded-xl border-white/15 bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-purple-400/60 focus-within:border-purple-400/70 focus-within:ring-purple-500/40"
              }`}
              style={{
                boxShadow:
                  isCustomerFacingSearch
                    ? "0 8px 20px rgba(15,23,42,0.08), 0 0 0 0 rgba(124,58,237,0)"
                    : "inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 0 rgba(124,58,237,0)",
              }}
            >
                {isCustomerFacingSearch ? (
                  <div
                    className="relative flex h-full shrink-0 items-center border-r border-slate-200/80"
                    ref={publicDesktopLocationRef}
                  >
                    <button
                      type="button"
                      onClick={() => setLocationOpen((open) => !open)}
                      className="flex h-full max-w-[190px] items-center gap-2 rounded-l-2xl px-4 text-left text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500/30 xl:max-w-[220px]"
                      aria-haspopup="dialog"
                      aria-expanded={locationOpen}
                    >
                      <MapPin className="h-4 w-4 shrink-0 text-purple-600" />
                      <span className="min-w-0 truncate text-sm font-medium">{locationLabel}</span>
                      <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                    </button>
                    {renderLocationDropdown("left-0 w-72", "global-location-desktop")}
                  </div>
                ) : null}
                {!isCustomerFacingSearch ? (
                <div
                  className={`hidden h-full lg:flex items-center gap-2 border-r bg-white/5 px-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70 transition-colors duration-200 ease-out hover:bg-purple-500/10 ${
                    selectedCategory !== "All" ? "border-purple-400/70" : "border-white/10"
                  }`}
                >
                  <label htmlFor="global-search-category" className="sr-only">
                    Category
                  </label>
                  <div className="relative">
                    <SearchCategoryDropdown
                      id="global-search-category"
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      options={SEARCH_CATEGORIES}
                      widthCh={categorySelectWidth}
                    />
                  </div>
                </div>
                ) : null}
                <div className="relative flex-1">
                  <Search
                    className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors duration-200 ease-out hover:text-purple-400 ${
                      isCustomerFacingSearch ? "text-slate-400" : "text-white/60"
                    }`}
                  />
                  <input
                    id="global-nav-search"
                    name="search"
                    ref={searchInputRef}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setSuggestionsOpen(hasHybridResults || searchTerm.trim().length > 0)}
                    onFocusCapture={(event) => {
                      event.currentTarget.form?.style.setProperty(
                        "box-shadow",
                        isCustomerFacingSearch
                          ? "0 8px 20px rgba(15,23,42,0.08), 0 0 0 2px rgba(124,58,237,0.25)"
                          : "inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 2px rgba(124,58,237,0.25)"
                      );
                    }}
                    onBlurCapture={(event) => {
                      event.currentTarget.form?.style.setProperty(
                        "box-shadow",
                        isCustomerFacingSearch
                          ? "0 8px 20px rgba(15,23,42,0.08), 0 0 0 0 rgba(124,58,237,0)"
                          : "inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 0 rgba(124,58,237,0)"
                      );
                    }}
                    className={`h-full w-full bg-transparent pl-11 text-sm focus:outline-none ${
                      isCustomerFacingSearch
                        ? "pr-3 text-slate-950 placeholder:text-slate-500"
                        : "pr-4 text-white placeholder:text-white/60"
                    }`}
                    placeholder="Search local makers, shops, and pickup favorites..."
                    aria-label="Search shops and listings"
                    type="search"
                  />
                </div>
                <button
                  type="submit"
                  className={`m-1.5 min-w-[92px] rounded-xl px-5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-purple-500/35 ${
                    isCustomerFacingSearch
                      ? "bg-purple-600 text-white shadow-[0_6px_14px_rgba(124,58,237,0.25)] hover:bg-purple-700"
                      : "m-0 h-full rounded-none bg-white text-black hover:bg-white/90 yb-navbar-light"
                  }`}
                >
                  Search
                </button>
              </form>

              {suggestionsOpen && (searchLoading || searchError || hasHybridResults) ? (
                <div className="absolute left-0 right-0 top-full mt-2 z-50">
                <div className="rounded-2xl p-3 yb-dropdown-surface">
                  {searchError ? (
                      <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-xs text-rose-200 mb-2">
                        {searchError}
                      </div>
                    ) : null}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] yb-dropdown-muted">
                          <PackageSearch className="h-4 w-4" />
                          Items
                          {searchLoading ? (
                            <YourBarrioLoader size="xs" className="yb-dropdown-muted" />
                          ) : null}
                        </div>
                        <div className="space-y-2">
                          {sortedSearchItems.slice(0, 4).map((item) => (
                            <button
                              key={`item-${item.id}`}
                              type="button"
                              onClick={() => handleSuggestionSelect(item.title, item.id)}
                              className="w-full text-left rounded-xl border border-white/10 bg-white/5 px-3 py-3 transition flex items-start gap-3 yb-dropdown-item"
                            >
                              <SearchResultThumbnail
                                src={item.photo_url}
                                testId="desktop-listing-image"
                                fallback={
                                  <div
                                    aria-hidden="true"
                                    data-testid="desktop-listing-fallback"
                                    className="h-10 w-10 shrink-0 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center text-[11px] font-semibold text-white/80"
                                  >
                                    {item.category
                                      ? item.category.slice(0, 3).toUpperCase()
                                      : "AI"}
                                  </div>
                                }
                              />
                              <div className="flex-1">
                                <div className="text-sm font-semibold leading-snug">{item.title}</div>
                                <div className="text-[11px] yb-dropdown-muted">
                                  {item.category || "Local listing"}
                                  {item.price ? ` · $${item.price}` : ""}
                                </div>
                                <SearchResultAvailability
                                  item={item}
                                  testId="desktop-item-availability"
                                />
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] yb-dropdown-muted">
                          <Store className="h-4 w-4" />
                          Businesses & places
                        </div>
                        <div className="space-y-2">
                          {(searchResults.businesses || []).slice(0, 3).map((biz) => (
                            <Link
                              key={`biz-${biz.id}`}
                              href={`/b/${encodeURIComponent(biz.id)}`}
                              onClick={closeSearchDropdown}
                              onKeyDown={(event) => handleBusinessSuggestionKeyDown(event, biz.id)}
                              tabIndex={0}
                              className="w-full text-left rounded-xl border border-white/10 bg-white/5 px-3 py-3 transition flex items-start gap-3 yb-dropdown-item cursor-pointer"
                            >
                              <SearchResultThumbnail
                                src={biz.image}
                                testId="desktop-shop-image"
                                fallback={
                                  <div
                                    aria-hidden="true"
                                    data-testid="desktop-shop-fallback"
                                    className="h-10 w-10 shrink-0 rounded-lg bg-emerald-500/20 border border-emerald-200/30 flex items-center justify-center"
                                  >
                                    <Store
                                      aria-hidden="true"
                                      className="h-4 w-4 text-emerald-200"
                                    />
                                  </div>
                                }
                              />
                              <div className="flex-1">
                                <div className="text-sm font-semibold leading-snug">{biz.name}</div>
                                <div className="text-[11px] yb-dropdown-muted">
                                  {biz.category || "Local business"}
                                  {biz.city ? ` · ${biz.city}` : ""}
                                </div>
                              </div>
                            </Link>
                          ))}

                          {(searchResults.places || []).slice(0, 3).map((place) => (
                            <button
                              key={`place-${place.id}`}
                              type="button"
                              onClick={() => handleSuggestionSelect(place.name)}
                              className="w-full text-left rounded-xl border border-white/10 bg-white/5 px-3 py-3 transition flex items-start gap-3 yb-dropdown-item"
                            >
                              <div className="h-10 w-10 rounded-lg bg-blue-500/20 border border-blue-200/30 flex items-center justify-center">
                                <MapPin className="h-4 w-4 text-blue-100" />
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-semibold leading-snug">{place.name}</div>
                                <div className="text-[11px] yb-dropdown-muted">
                                  {place.address || "Nearby result"}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="hidden lg:flex flex-1" />
        )}

        {!minimal ? (
          <div className="hidden items-center gap-4 lg:flex xl:gap-5">
            <CartNavActionClient />
            <HeaderAccountWidget surface={surface} variant="desktop" forcedAuth={forcedAuth} />
          </div>
        ) : null}

      </div>

      {!minimal ? (
        <HeaderAccountWidget
          surface={surface}
          variant="mobile"
          forcedAuth={forcedAuth}
          mobileMenuOpen={mobileMenuOpen}
          onCloseMobileMenu={() => setMobileMenuOpen(false)}
          mobileDrawerId={mobileDrawerId}
        />
      ) : null}
    </nav>
  );
}
